import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AccountPaymentMethodsApiPropertiesModel, PaymentMethodDto } from '@/services/platform/api/model';
import { Icon } from '@/components/ui/icon';
import { Button } from '@/components/ui/button';
import { Toast } from '@/components/ui/toast';
import { SpinLoading } from '@/components/shared/loadings/spin';
import { useClientTranslation } from '@/lib/core/i18n';
import { useLocaleStore } from '@/lib/core/stores';
import { ProfilePaymentEdit } from './edit';
import { formatCardBrand, formatExpiry } from './utils';

/** Toast intent handed back by the add page through router state. */
type PaymentToastIntent = 'added' | 'discarded';

interface ProfilePaymentListProps {
  paymentMethods: PaymentMethodDto[] | undefined;
  /** Properties of the `accountPaymentMethodsApi` element (list screen). */
  properties: AccountPaymentMethodsApiPropertiesModel | undefined;
  loading?: boolean;
  /** True when the list request failed, so an error is shown instead of the "no cards" empty state. */
  loadFailed?: boolean;
  onUpdate: (
    id: string,
    expMonth: number,
    expYear: number,
    holderName: string,
    isPreferred: boolean
  ) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
}

interface PaymentMethodCardProps {
  paymentMethod: PaymentMethodDto;
  preferredLabel: string;
  editLabel: string;
  expiryLabel: (month: string, year: string) => string;
  cardLabel: string;
  onEdit: (paymentMethod: PaymentMethodDto) => void;
}

function PaymentMethodCard({
  paymentMethod,
  preferredLabel,
  editLabel,
  expiryLabel,
  cardLabel,
  onEdit,
}: PaymentMethodCardProps) {
  const expiry = formatExpiry(paymentMethod.expMonth, paymentMethod.expYear);

  return (
    <div className="border-jncquoi-neutral-200 flex flex-col gap-2 border-b-[0.5px] pt-3.5 pb-4">
      {paymentMethod.isPreferred && (
        <div className="flex items-center gap-2">
          <Icon name="star-solid" width={12} height={12} className="text-jncquoi-neutral-700 shrink-0" />
          <p className="font-pp-neue-montreal text-jncquoi-neutral-700 text-[10px] font-semibold tracking-[0.5px] uppercase">
            {preferredLabel}
          </p>
        </div>
      )}
      <div className="flex w-full items-center gap-2">
        <p className="font-pp-neue-montreal text-jncquoi-neutral-1000 min-w-0 flex-1 text-[14px] font-semibold tracking-[0.5px]">
          {cardLabel}
        </p>
        <button
          type="button"
          aria-label={editLabel}
          className="flex size-5 shrink-0 items-center justify-center"
          onClick={() => onEdit(paymentMethod)}
        >
          <Icon name="edit" width={20} height={20} className="text-jncquoi-neutral-1000" />
        </button>
      </div>
      <div className="font-pp-neue-montreal text-jncquoi-neutral-1000 flex flex-col text-[14px] tracking-[0.5px]">
        {paymentMethod.holderName && <p>{paymentMethod.holderName}</p>}
        {expiry && <p>{expiryLabel(expiry.month, expiry.year)}</p>}
        {paymentMethod.country && <p>{paymentMethod.country}</p>}
      </div>
    </div>
  );
}

/**
 * Saved payment methods screen — the list, its empty state, and the edit sheet.
 *
 * Adding a card navigates to the CMS-backed `/profile/payment/add-payment-method/` page rather than
 * opening an overlay: that page owns the authored subtitle, disclaimer and accepted-cards content,
 * which an overlay here has no way to read. Editing stays a bottom sheet, since it is per-card and
 * has no page of its own.
 */
export function ProfilePaymentList({
  paymentMethods,
  properties,
  loading = false,
  loadFailed = false,
  onUpdate,
  onDelete,
}: ProfilePaymentListProps) {
  const t = useClientTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const locale = useLocaleStore(state => state.locale);
  const [editing, setEditing] = useState<PaymentMethodDto | null>(null);
  const [toast, setToast] = useState<{ message: string; variant: 'dark' | 'error' } | null>(null);
  const [toastKey, setToastKey] = useState(0);
  const isEmpty = !paymentMethods || paymentMethods.length === 0;

  const language = locale?.split('-')[0] ?? 'en';
  const addPath = `/${language}/profile/payment/add-payment-method/`;

  // Re-mounting on each toast restarts the auto-dismiss timer, so repeated actions still animate.
  function showToast(message: string, variant: 'dark' | 'error' = 'dark') {
    setToast({ message, variant });
    setToastKey(key => key + 1);
  }

  // The add page lives on its own route, so it reports success/discard back through router state.
  // The state is cleared as soon as it is consumed, so a refresh doesn't replay the toast.
  const toastIntent = (location.state as { paymentToast?: PaymentToastIntent } | null)?.paymentToast;
  const pathname = location.pathname;
  // `t` (from `useClientTranslation`) is a fresh closure on every render, so it cannot go in the
  // deps — and neither can anything else render-unstable: re-running this effect bumps `toastKey`,
  // which remounts the toast and restarts its dismiss timer, leaving it on screen forever.
  //
  // The latch is never reset. Clearing the router state below already re-runs this effect, so a
  // resettable guard would just re-fire; and because the list unmounts when navigating to the add
  // page, a fresh mount gets a fresh latch — which is exactly the scope that should re-toast.
  const hasToastedRef = useRef(false);
  useEffect(() => {
    if (!toastIntent || hasToastedRef.current) return;
    hasToastedRef.current = true;
    setToast({
      message:
        toastIntent === 'added'
          ? t('App.Labels.PaymentMethodAdded')
          : t('App.Labels.DiscardPaymentMethodSuccess'),
      variant: 'dark',
    });
    setToastKey(key => key + 1);
    navigate(pathname, { replace: true, state: null });
  }, [toastIntent, pathname, navigate, t]);

  return (
    <>
      {/*
        Fills the viewport below the fixed ScreenHead (60px) that DefaultScreenLayout reserves.
        Both safe-area insets are subtracted because that layout applies `pt-safe-area` *and*
        `pb-safe-area` — omitting the bottom one left the page ~16px taller than the viewport.
      */}
      <div
        className="flex flex-col"
        style={{
          minHeight:
            'calc(100dvh - 60px - max(1rem, env(safe-area-inset-top)) - max(1rem, env(safe-area-inset-bottom)))',
        }}
      >
        {/* pb-28 clears the fixed CTA below (52px button + 48px offset) so the last card scrolls free. */}
        <div className="flex flex-1 flex-col px-4 pb-28">
          {loading ? (
            <SpinLoading wrapperClassName="flex-1 min-h-[30vh]" />
          ) : loadFailed ? (
            <div className="flex flex-1 flex-col items-center justify-center gap-2">
              <div className="flex size-11 items-center justify-center">
                <Icon name="warning-circle" width={20} height={20} className="text-jncquoi-neutral-1000" />
              </div>
              <p
                role="alert"
                className="font-pp-neue-montreal text-jncquoi-neutral-1000 max-w-64 text-center text-[14px] tracking-[0.5px]"
              >
                {t('App.Labels.PaymentMethodsLoadError')}
              </p>
            </div>
          ) : isEmpty ? (
            /* pt-30 = the 120px top offset the design specifies (Figma 2114:23933) — the message
               sits near the top rather than centred in the viewport. */
            <div className="flex flex-1 flex-col items-center gap-2 pt-30">
              <div className="flex size-11 items-center justify-center">
                <Icon name="credit-card" width={20} height={20} className="text-jncquoi-neutral-1000" />
              </div>
              {/* whitespace-pre-line renders the `\n` in the locale string, so the copy breaks
                  where the design breaks it instead of wherever the width happens to wrap. */}
              <p className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-center text-[12px] tracking-[0.5px] whitespace-pre-line uppercase">
                {t('App.Labels.NoPaymentMethods')}
              </p>
            </div>
          ) : (
            <div className="flex flex-1 flex-col gap-0 pt-4">
              {paymentMethods.map(paymentMethod => (
                <PaymentMethodCard
                  key={paymentMethod.id}
                  paymentMethod={paymentMethod}
                  preferredLabel={t('App.Labels.PreferredPaymentMethodLabel')}
                  editLabel={t('App.Labels.EditPaymentMethod')}
                  cardLabel={t('App.Labels.CardEndingIn', {
                    brand: formatCardBrand(paymentMethod.brand),
                    last4: paymentMethod.last4 ?? '',
                  })}
                  expiryLabel={(month, year) => t('App.Labels.CardExpires', { month, year })}
                  onEdit={setEditing}
                />
              ))}
            </div>
          )}
        </div>

        {/* pb-12 = the 48px gap to the bottom of the screen specified in the design. */}
        <div className="fixed inset-x-0 bottom-0 bg-white px-4 pb-12">
          <Button
            variant="primary-solid"
            palette="dark"
            size="lg"
            className="w-full"
            onClick={() => navigate(addPath)}
          >
            {t('App.Labels.AddNewPaymentMethod')}
          </Button>
        </div>
      </div>

      <ProfilePaymentEdit
        paymentMethod={editing}
        title={properties?.updateCardTitle}
        privacyPolicyDisclaimer={properties?.privacyPolicyDisclaimer}
        onOpenChange={open => {
          if (!open) setEditing(null);
        }}
        onUpdate={async (expMonth, expYear, holderName, isPreferred) => {
          if (!editing?.id) return;
          // Rethrow so the sheet stays open and shows its own inline error; the toast covers the
          // case where the sheet has already closed.
          try {
            await onUpdate(editing.id, expMonth, expYear, holderName, isPreferred);
          } catch (error) {
            showToast(t('App.Labels.PaymentMethodUpdateError'), 'error');
            throw error;
          }
          setEditing(null);
          showToast(t('App.Labels.PaymentMethodUpdated'));
        }}
        onDelete={async () => {
          if (!editing?.id) return;
          try {
            await onDelete(editing.id);
          } catch (error) {
            showToast(t('App.Labels.PaymentMethodDeleteError'), 'error');
            throw error;
          }
          setEditing(null);
          showToast(t('App.Labels.PaymentMethodDeleted'));
        }}
      />

      <Toast
        key={toastKey}
        open={toast !== null}
        message={toast?.message ?? ''}
        variant={toast?.variant}
        onDismiss={() => setToast(null)}
      />
    </>
  );
}
